import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { TrendingUp, Trophy, Zap } from 'lucide-react';

const API = import.meta.env.VITE_API_URL?.replace('/v1', '') || 'http://64.23.240.10/api';

interface Protocol {
  id: number;
  name: string;
  slug: string;
  chain: string;
  category: string;
  apy: string;
}

const CATEGORIES = [
  { key: 'all', label: 'All Protocols' },
  { key: 'native-staking', label: 'Native Staking' },
  { key: 'liquid-staking', label: 'Liquid Staking' },
  { key: 'defi-yield', label: 'DeFi Yield' },
];

const getRankStyle = (rank: number) => {
  if (rank === 1) return { bg: 'bg-yellow-400/20 border-yellow-400/50', text: 'text-yellow-400', medal: '🥇' };
  if (rank === 2) return { bg: 'bg-gray-400/20 border-gray-400/50', text: 'text-gray-400', medal: '🥈' };
  if (rank === 3) return { bg: 'bg-orange-400/20 border-orange-400/50', text: 'text-orange-400', medal: '🥉' };
  return { bg: 'bg-steel-blue/10 border-sky-blue/10', text: 'text-sky-blue', medal: `#${rank}` };
};

const getCategoryColor = (category: string) => {
  if (category === 'native-staking') return 'text-green-400 bg-green-400/10 border-green-400/20';
  if (category === 'liquid-staking') return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
  return 'text-neon-red bg-neon-red/10 border-neon-red/20';
};

const getCategoryLabel = (category: string) => {
  if (category === 'native-staking') return 'Native';
  if (category === 'liquid-staking') return 'Liquid';
  return 'DeFi';
};

export default function LeaderboardPage() {
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [amount, setAmount] = useState(1000);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/leaderboard?category=${category}&limit=50`)
      .then(r => r.json())
      .then(d => { setProtocols(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [category]);

  const top3 = protocols.slice(0, 3);


  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO
        title="Highest APY Crypto Staking — Live Leaderboard 2026"
        description="See which crypto staking protocols are paying the highest APY right now. Live leaderboard of 156+ protocols ranked by staking rewards. Updated every 15 minutes."
        canonical="https://www.residualvault.com/leaderboard"
        keywords="highest APY crypto staking, best staking rewards 2026, highest staking APY, top staking protocols, best crypto yield 2026"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-10 h-10 text-yellow-400" />
            <h1 className="text-5xl font-bold">
              <span className="text-cream">APY </span>
              <span className="gradient-text-neon">Leaderboard</span>
            </h1>
          </div>
          <p className="text-xl text-sky-blue max-w-2xl mx-auto">
            Live ranking of the highest yielding staking protocols. Updated every 15 minutes.
          </p>
        </div>

        {/* Calculator row */}
        <div className="card-glass mb-8 flex items-center gap-4 flex-wrap">
          <Zap className="w-5 h-5 text-neon-red shrink-0" />
          <span className="text-sky-blue text-sm">Calculate returns for</span>
          <div className="flex items-center gap-2">
            <span className="text-sky-blue">$</span>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              className="bg-navy-dark border border-sky-blue/30 rounded-lg px-3 py-1.5 text-cream w-28 focus:outline-none focus:border-neon-red transition-colors"
            />
          </div>
          <span className="text-sky-blue text-sm">staked — see yearly earnings in the table</span>
        </div>

        {/* Category filters */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c.key} onClick={() => setCategory(c.key)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${category === c.key ? 'bg-gradient-cta text-white shadow-neon' : 'bg-steel-blue/20 text-sky-blue hover:bg-steel-blue/30'}`}>
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="card-glass h-16 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Top 3 podium */}
            {top3.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mb-8">
                {top3.map((p, i) => {
                  const rank = getRankStyle(i + 1);
                  const apy = parseFloat(p.apy);
                  const yearly = (amount * apy) / 100;
                  return (
                    <Link key={p.id} to={`/protocol/${p.slug}`}
                      className={`card-premium border ${rank.bg} text-center hover:scale-105 transition-transform`}>
                      <div className="text-4xl mb-2">{rank.medal}</div>
                      <div className="w-12 h-12 bg-gradient-cta rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-3 shadow-neon">
                        {p.name[0]}
                      </div>
                      <div className="text-cream font-bold text-lg mb-1">{p.name}</div>
                      <div className={`text-xs px-2 py-0.5 rounded-full border inline-block mb-3 ${getCategoryColor(p.category)}`}>
                        {getCategoryLabel(p.category)}
                      </div>
                      <div className={`text-4xl font-bold ${rank.text} mb-1`}>{apy.toFixed(2)}%</div>
                      <div className="text-sky-blue text-xs">APY</div>
                      <div className="mt-3 pt-3 border-t border-sky-blue/10">
                        <div className="text-cream font-semibold">${yearly.toFixed(2)}/yr</div>
                        <div className="text-sky-blue/60 text-xs">on ${amount.toLocaleString()}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Full table */}
            <div className="card-premium overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-cream flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-neon-red" />
                  Full Rankings
                </h2>
                <span className="text-sky-blue text-sm">{protocols.length} protocols</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-sky-blue/20">
                      {['Rank', 'Protocol', 'Chain', 'Type', 'APY', `Yearly ($${amount.toLocaleString()})`, 'Monthly'].map(h => (
                        <th key={h} className="text-left py-3 px-4 text-sky-blue text-xs uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {protocols.map((p, i) => {
                      const rank = getRankStyle(i + 1);
                      const apy = parseFloat(p.apy);
                      const yearly = (amount * apy) / 100;
                      const monthly = yearly / 12;
                      return (
                        <tr key={p.id} className="border-b border-sky-blue/10 hover:bg-steel-blue/10 transition-colors">
                          <td className="py-3 px-4">
                            <span className={`font-bold text-lg ${rank.text}`}>{rank.medal}</span>
                          </td>
                          <td className="py-3 px-4">
                            <Link to={`/protocol/${p.slug}`} className="flex items-center gap-3 hover:text-neon-red transition-colors">
                              <div className="w-8 h-8 bg-gradient-cta rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-neon shrink-0">
                                {p.name[0]}
                              </div>
                              <span className="text-cream font-semibold">{p.name}</span>
                            </Link>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-steel-blue/20 border border-sky-blue/20 rounded text-sky-blue text-xs font-semibold">{p.chain}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded border text-xs ${getCategoryColor(p.category)}`}>
                              {getCategoryLabel(p.category)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="gradient-text-neon font-bold text-xl">{apy.toFixed(2)}%</span>
                          </td>
                          <td className="py-3 px-4 text-cream font-semibold">${yearly.toFixed(2)}</td>
                          <td className="py-3 px-4 text-sky-blue">${monthly.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CTA */}
            <div className="card-premium text-center mt-8">
              <h2 className="text-2xl font-bold mb-3">
                <span className="text-cream">Want to compare these protocols </span>
                <span className="gradient-text-neon">side by side?</span>
              </h2>
              <p className="text-sky-blue mb-6">Use our staking calculator to compare up to 5 protocols with compound interest over 1-10 years.</p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/calculator" className="btn-neon inline-flex items-center">Open Calculator →</Link>
                <Link to="/staking" className="bg-steel-blue/20 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-flex items-center">
                  Browse all protocols
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
